import React, { useState, useEffect } from 'react';
import { BrowserRouter, useLocation } from 'react-router-dom';
import '@/App.css';
import { Sidebar } from '@/components/Sidebar';
import { ChatInterface } from '@/components/ChatInterface';
import { LoginPage } from '@/components/LoginPage';
import { useTheme } from '@/lib/theme';
import { Toaster } from '@/components/ui/sonner';
import api, { saveSessionToken, clearSessionToken, getSessionToken } from '@/lib/api';
import { Loader2 } from 'lucide-react';
import { requestNotificationPermission } from '@/lib/notifications';

// Auth context to share user state
const AuthContext = React.createContext(null);

export function useAuth() {
  return React.useContext(AuthContext);
}

// Loading spinner component
function LoadingSpinner({ message = "Laden..." }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center">
        <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-primary" />
        <p className="text-muted-foreground">{message}</p>
      </div>
    </div>
  );
}

// Main chat application
function MainApp() {
  const { theme, setTheme } = useTheme();
  const [currentConversationId, setCurrentConversationId] = useState(null);
  const [refreshSidebar, setRefreshSidebar] = useState(0);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, logout } = useAuth();

  const handleNewChat = () => {
    setCurrentConversationId(null);
    setSidebarOpen(false); // Close sidebar on mobile after action
  };

  const handleSelectConversation = (id) => {
    setCurrentConversationId(id);
    setSidebarOpen(false); // Close sidebar on mobile after selection
  };

  const handleConversationUpdate = (id) => {
    if (!currentConversationId) {
      setCurrentConversationId(id);
    }
    setRefreshSidebar(prev => prev + 1);
  };

  return (
    <div className="flex h-screen overflow-hidden">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
      
      {/* Sidebar - hidden on mobile by default, shown as overlay when open */}
      <div className="hidden md:block">
        <Sidebar
          currentConversationId={currentConversationId}
          onSelectConversation={handleSelectConversation}
          onNewChat={handleNewChat}
          theme={theme}
          setTheme={setTheme}
          refreshTrigger={refreshSidebar}
          user={user}
          onLogout={logout}
          isOpen={true}
          onClose={() => {}}
        />
      </div>
      
      {/* Mobile Sidebar Overlay */}
      <div className={`md:hidden fixed inset-y-0 left-0 z-50 transform transition-transform duration-300 ease-in-out ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <Sidebar
          currentConversationId={currentConversationId}
          onSelectConversation={handleSelectConversation}
          onNewChat={handleNewChat}
          theme={theme}
          setTheme={setTheme}
          refreshTrigger={refreshSidebar}
          user={user}
          onLogout={logout}
          isOpen={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
        />
      </div>
      
      <ChatInterface
        conversationId={currentConversationId}
        theme={theme}
        onConversationUpdate={handleConversationUpdate}
        onSelectConversation={handleSelectConversation}
        onOpenSidebar={() => setSidebarOpen(true)}
      />
    </div>
  );
}

// Auth provider component
function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processingCallback, setProcessingCallback] = useState(false);
  const [authError, setAuthError] = useState(null);

  // Check for session_id in URL hash (OAuth callback)
  useEffect(() => {
    const processAuthCallback = async () => {
      const hash = window.location.hash;
      if (hash && hash.includes('session_id=')) {
        setProcessingCallback(true);
        const sessionId = new URLSearchParams(hash.substring(1)).get('session_id');
        
        if (sessionId) {
          try {
            const response = await api.post('/auth/session', { session_id: sessionId });
            // Save session token to localStorage as backup
            if (response.data.session_token) {
              saveSessionToken(response.data.session_token);
            }
            setUser(response.data);
            // Clear the hash from URL
            window.history.replaceState(null, '', window.location.pathname);
          } catch (error) {
            console.error('Auth callback error:', error);
            if (error.response?.status === 403) {
              setAuthError(error.response.data.detail || 'Zugriff verweigert');
            }
            // Clear hash even on error
            window.history.replaceState(null, '', window.location.pathname);
          }
        }
        setProcessingCallback(false);
        setLoading(false);
        return;
      }
      
      // No callback - check existing session (via cookie or localStorage token)
      checkExistingAuth();
    };

    const checkExistingAuth = async () => {
      try {
        // Check if we have a stored token or cookie
        const hasToken = getSessionToken();
        if (!hasToken) {
          // No token stored, skip the API call
          setUser(null);
          setLoading(false);
          return;
        }
        
        const response = await api.get('/auth/me');
        setUser(response.data);
      } catch (error) {
        // Not authenticated - clear any stored token
        clearSessionToken();
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    processAuthCallback();
  }, []);

  const login = (userData) => {
    setUser(userData);
    setAuthError(null);
    // Request notification permission after successful login
    requestNotificationPermission();
  };

  const logout = async () => {
    try {
      await api.post('/auth/logout', {});
    } catch (error) {
      console.error('Logout error:', error);
    }
    clearSessionToken();
    setUser(null);
  };

  const clearError = () => {
    setAuthError(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, processingCallback, authError, login, logout, clearError }}>
      {children}
    </AuthContext.Provider>
  );
}

// Router content - handles all routing logic
function AppRoutes() {
  const { user, loading, processingCallback, authError, clearError } = useAuth();
  const location = useLocation();

  // Show loading while checking auth or processing callback
  if (loading || processingCallback) {
    return <LoadingSpinner message={processingCallback ? "Anmeldung wird verarbeitet..." : "Laden..."} />;
  }

  // If authenticated, show main app
  if (user) {
    return <MainApp />;
  }

  // Not authenticated - show login page with potential error
  return <LoginPage error={authError} onClearError={clearError} />;
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster position="top-right" />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
